"use client";

import { formatTagLabel } from "@/lib/clubs";

interface ClubFiltersProps {
  states: string[];
  characteristics: string[];
  search: string;
  selectedState: string | null;
  selectedCharacteristics: string[];
  onSearchChange: (value: string) => void;
  onStateChange: (state: string | null) => void;
  onCharacteristicToggle: (characteristic: string) => void;
  onClear: () => void;
}

export function ClubFilters({
  states,
  characteristics,
  search,
  selectedState,
  selectedCharacteristics,
  onSearchChange,
  onStateChange,
  onCharacteristicToggle,
  onClear,
}: ClubFiltersProps) {
  const hasFilters =
    search !== "" ||
    selectedState !== null ||
    selectedCharacteristics.length > 0;

  return (
    <div className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <div className="flex flex-wrap gap-4">
        {/* Text search */}
        <div className="min-w-[200px] flex-1">
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
            Search
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search clubs..."
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
          />
        </div>

        {/* State filter */}
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
            State / Region
          </label>
          <select
            value={selectedState ?? ""}
            onChange={(e) => onStateChange(e.target.value || null)}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="">All states</option>
            {states.map((s) => (
              <option key={s} value={s}>
                {formatTagLabel(s)}
              </option>
            ))}
          </select>
        </div>

        {hasFilters && (
          <div className="flex items-end">
            <button
              onClick={onClear}
              className="rounded-md px-3 py-1.5 text-sm text-gray-500 hover:text-primary dark:text-gray-400"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Characteristic pill toggles */}
      <div>
        <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">
          Program Type
        </label>
        <div className="flex flex-wrap gap-2">
          {characteristics.map((char) => {
            const isSelected = selectedCharacteristics.includes(char);
            return (
              <button
                key={char}
                onClick={() => onCharacteristicToggle(char)}
                className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                  isSelected
                    ? "border-primary bg-primary text-white"
                    : "border-gray-300 text-gray-600 hover:border-primary hover:text-primary dark:border-gray-600 dark:text-gray-300"
                }`}
              >
                {formatTagLabel(char)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
